import React, { useState, useEffect } from 'react';
import { DndProvider } from 'react-dnd';
import HTML5Backend from 'react-dnd-html5-backend';
import TouchBackend from 'react-dnd-touch-backend';
import styles from './Game.module.scss';
import Slot from '../board/Slot';
import useDebounce from '../../hooks/useDebounce';
import XimiXurriButton from '../buttons/XimiXurriButton';
import PlayerDeck from '../deck/PlayerDeck';
import DraggableCard from '../card/DraggableCard';
import { useCallback } from 'react';
import useBot from '../../hooks/useBot';

// simple way to check whether the device support touch (it doesn't check all fallback, it supports only modern browsers)
const isTouchDevice = () => {
  if ('ontouchstart' in window) {
    return true;
  }
  return false;
};

// Assigning backend based on touch support on the device
const backendForDND = isTouchDevice() ? TouchBackend : HTML5Backend;

const Game = () => {
  const deckSlots = [
    { special: 'slot', num: 1 },
    { special: 'slot', num: 2 },
    { special: 'slot', num: 3 },
    { special: 'slot', num: 4 }
  ];

  const [deck, setDeck] = useState([]);
  const [player1Cards, setPlayer1Cards] = useState([]);
  const [player2Cards, setPlayer2Cards] = useState([]);
  const [player1Deck, setPlayer1Deck] = useState(deckSlots);
  const [player2Deck, setPlayer2Deck] = useState(deckSlots);
  const [leftDeck, setLeftDeck] = useState([{ special: 'slot' }]);
  const [rightDeck, setRightDeck] = useState([{ special: 'slot' }]);

  const [status, setStatus] = useState('paused');

  const [picked, setPicked] = useState({
    player1: false,
    player2: false
  });

  const [winner, setWinner] = useState(null);

  const [xxPlayer1, setXxPlayer1] = useState(false);
  const [xxPlayer2, setXxPlayer2] = useState(false);

  const roundDone = useDebounce(xxPlayer1 || xxPlayer2, 1000);

  const isXimiXurri = () => {
    const topLeft = leftDeck[leftDeck.length - 1];
    const topRight = rightDeck[rightDeck.length - 1];
    return (
      topLeft.special !== 'slot' &&
      topRight.special !== 'slot' &&
      topLeft.num === topRight.num
    );
  };

  const pushXimiXurri = player => {
    console.log('pushed', player, roundDone, isXimiXurri());
    if (!roundDone && isXimiXurri()) {
      if (player === 1) {
        setXxPlayer1(true);
      } else {
        setXxPlayer2(true);
      }
    }
  };

  const canMoveToDeck = (item, playerCards, card, player) => {
    if (status !== 'playing' || typeof item.index !== 'undefined') {
      return false;
    }
    return (
      item.player === player &&
      card.special === 'slot' &&
      playerCards.length > 0
    );
  };

  const moveToDeck = (setPlayerCards, setPlayerDeck, index) => {
    setPlayerCards(cards => {
      setPlayerDeck(slots => {
        const ret = [...slots];
        ret[index] = cards[cards.length - 1];
        return ret;
      });
      return cards.slice(0, -1);
    });
  };

  const canMoveToGameDeck = (item, deck) => {
    if (status !== 'playing') {
      return false;
    }
    // Item must be from decks
    const found =
      item.player === 1 ? player1Deck[item.index] : player2Deck[item.index];
    if (!found) {
      return false;
    }

    const top = deck[deck.length - 1];
    if (top.special === 'slot') {
      return true;
    }
    const diff = Math.abs(top.num - item.card.num);
    return diff === 1 || diff === 11;
  };

  const moveToGameDeck = (item, setDeck) => {
    const updatePlayerDeck = playerDeck => {
      const ret = [...playerDeck];
      ret[item.index] = { special: 'slot', num: item.index + 1 };
      return ret;
    };
    if (item.player === 1) {
      setPlayer1Deck(updatePlayerDeck);
      setDeck(deck => [...deck, player1Deck[item.index]]);
    } else {
      setPlayer2Deck(updatePlayerDeck);
      setDeck(deck => [...deck, player2Deck[item.index]]);
    }
  };

  const canPickUpDeck = (item, player) =>
    status === 'blocked' && item.idDeck && !picked[`player${player}`];

  const pickUpDeck = (idDeck, setCards, player) => {
    if (idDeck === 'left') {
      setCards(cards => [...leftDeck.slice(1), ...cards]);
      setLeftDeck([{ special: 'slot' }]);
    } else if (idDeck === 'right') {
      setCards(cards => [...rightDeck.slice(1), ...cards]);
      setRightDeck([{ special: 'slot' }]);
    }
    setPicked(pkd => ({ ...pkd, [`player${player}`]: true }));
  };

  useBot(
    true,
    player2Cards,
    setPlayer2Cards,
    player2Deck,
    setPlayer2Deck,
    leftDeck,
    setLeftDeck,
    rightDeck,
    setRightDeck,
    canMoveToDeck,
    moveToDeck,
    canMoveToGameDeck,
    moveToGameDeck,
    canPickUpDeck,
    pickUpDeck,
    status,
    pushXimiXurri,
    isXimiXurri
  );

  const createDeck = useCallback(() => {
    const d = ['ors', 'copes', 'espases', 'bastos']
      .map(pal => [...Array(12).keys()].map(num => ({ pal, num: num + 1 })))
      .flat();
    // eslint-disable-next-line no-plusplus
    for (let i = d.length; i--; ) {
      const j = Math.floor(Math.random() * d.length);
      [d[i], d[j]] = [d[j], d[i]];
    }
    setDeck(d);
  }, []);

  const playAgain = () => {
    setStatus('paused');
    setPlayer1Deck(deckSlots);
    setPlayer2Deck(deckSlots);
    setLeftDeck([{ special: 'slot' }]);
    setRightDeck([{ special: 'slot' }]);
    setPicked({
      player1: false,
      player2: false
    });
    setXxPlayer1(false);
    setXxPlayer2(false);
    createDeck();
    setWinner(null);
  };

  useEffect(() => {
    createDeck();
  }, [createDeck]);

  // Set player cards
  useEffect(() => {
    setPlayer1Cards(deck.slice(0, deck.length / 2));
    setPlayer2Cards(deck.slice(deck.length / 2, deck.length));
    setStatus('playing');
    console.log('deck', deck);
  }, [deck]);

  useEffect(() => {
    const moreMoves = () => {
      if (
        player1Deck.filter(card => card.special === 'slot').length > 0 &&
        player1Cards.length > 0
      ) {
        return true;
      }
      if (
        player2Deck.filter(card => card.special === 'slot').length > 0 &&
        player2Cards.length > 0
      ) {
        return true;
      }
      const topLeft = leftDeck[leftDeck.length - 1];
      const topRight = rightDeck[rightDeck.length - 1];
      if (topLeft.special === 'slot' || topRight.special === 'slot') {
        return true;
      }

      if (
        (player1Deck.every(card => card.special === 'slot') &&
          player1Cards.length === 0) ||
        (player2Deck.every(card => card.special === 'slot') &&
          player2Cards.length === 0)
      ) {
        return false;
      }

      const playableCards = [
        ...player1Deck.filter(card => card.special !== 'slot'),
        ...player2Deck.filter(card => card.special !== 'slot')
      ];
      const canMove = playableCards.some(card => {
        const diff = Math.abs(topLeft.num - card.num);
        if (diff === 1 || diff === 11) {
          return true;
        }
        const diff2 = Math.abs(topRight.num - card.num);
        if (diff2 === 1 || diff2 === 11) {
          return true;
        }

        return false;
      });

      return canMove;
    };

    if (!moreMoves()) {
      setStatus('blocked');
    }
  }, [
    player1Cards,
    player1Deck,
    player2Cards,
    player2Deck,
    leftDeck,
    rightDeck
  ]);

  useEffect(() => {
    if (roundDone) {
      if (xxPlayer1 && xxPlayer2) {
        setStatus('blocked');
      } else {
        setStatus('ximixurri');
      }
    }
  }, [xxPlayer1, xxPlayer2, roundDone]);

  useEffect(() => {
    if (status !== 'playing') {
      let win = false;
      if (status === 'ximixurri') {
        if (xxPlayer1) {
          if (
            player1Cards.length === 0 &&
            player1Deck.every(card => card.special === 'slot')
          ) {
            setWinner('Player 1');
            win = true;
          } else {
            setPlayer2Cards(cards => [
              ...leftDeck.slice(1),
              ...rightDeck.slice(1),
              ...cards
            ]);
            setLeftDeck([{ special: 'slot' }]);
            setRightDeck([{ special: 'slot' }]);
            setXxPlayer1(false);
          }
        } else {
          if (
            player2Cards.length === 0 &&
            player2Deck.every(card => card.special === 'slot')
          ) {
            setWinner('Player 2');
            win = true;
          } else {
            setPlayer1Cards(cards => [
              ...leftDeck.slice(1),
              ...rightDeck.slice(1),
              ...cards
            ]);
            setLeftDeck([{ special: 'slot' }]);
            setRightDeck([{ special: 'slot' }]);
            setXxPlayer2(false);
          }
        }
        if (!win) {
          setStatus('playing');
        }
      }
    }
  }, [
    status,
    xxPlayer1,
    xxPlayer2,
    player1Cards,
    player2Cards,
    player1Deck,
    player2Deck,
    leftDeck,
    rightDeck,
    winner
  ]);

  useEffect(() => {
    console.log('And the winner is: ', winner);
  }, [winner]);

  useEffect(() => {
    console.log('Status: ', status);
  }, [status]);

  useEffect(() => {
    const debug = false;
    if (debug) {
      console.log('p1', player1Cards);
      console.log('p2', player2Cards);
    }
  }, [player1Cards, player2Cards]);

  useEffect(() => {
    const debug = false;
    if (debug) {
      console.table('p1', player1Deck);
      console.table('p2', player2Deck);
    }
  }, [player1Deck, player2Deck]);

  useEffect(() => {
    const debug = false;
    if (debug) {
      console.table('left', leftDeck);
      console.table('right', rightDeck);
    }
  }, [leftDeck, rightDeck]);

  useEffect(() => {
    if (picked.player1 && picked.player2) {
      setStatus('playing');
      setPicked({
        player1: false,
        player2: false
      });
      setXxPlayer1(false);
      setXxPlayer2(false);
    }
  }, [picked]);

  return (
    <>
      <DndProvider backend={backendForDND}>
        <div className={styles.Board}>
          <div className={styles.row}>
            <div className={styles.col}>
              <Slot
                moveCard={item => pickUpDeck(item.idDeck, setPlayer2Cards, 2)}
                canMoveCard={item => canPickUpDeck(item, 2)}
              >
                <DraggableCard
                  card={{ special: player2Cards.length ? 'reverse' : 'slot' }}
                  player={2}
                />
              </Slot>
            </div>
            <div className={styles.counter}>
              <h2>{player2Cards.length}</h2>
            </div>
          </div>
          <div className={styles.row}>
            <PlayerDeck
              player={2}
              playerDeck={player2Deck}
              moveCard={index =>
                moveToDeck(setPlayer2Cards, setPlayer2Deck, index)
              }
              canMoveCard={(item, card, player) =>
                canMoveToDeck(item, player2Cards, card, player)
              }
            />
          </div>
          <div className={styles.row}>
            <div className={styles.counter}>
              <XimiXurriButton player={2} onClick={pushXimiXurri} />
            </div>
            <div className={styles.counter}>
              <h2>{leftDeck.length - 1}</h2>
            </div>
            <div className={styles.col}>
              <Slot
                moveCard={item => moveToGameDeck(item, setLeftDeck)}
                canMoveCard={item => canMoveToGameDeck(item, leftDeck)}
              >
                <DraggableCard
                  card={leftDeck[leftDeck.length - 1]}
                  idDeck="left"
                />
              </Slot>
            </div>
            <div className={styles.col}>
              <Slot
                moveCard={item => moveToGameDeck(item, setRightDeck)}
                canMoveCard={item => canMoveToGameDeck(item, rightDeck)}
              >
                <DraggableCard
                  card={rightDeck[rightDeck.length - 1]}
                  idDeck="right"
                />
              </Slot>
            </div>
            <div className={styles.counter}>
              <h2>{rightDeck.length - 1}</h2>
            </div>
            <div className={styles.counter}>
              <XimiXurriButton player={1} onClick={pushXimiXurri} />
            </div>
          </div>
          <div className={styles.row}>
            <PlayerDeck
              player={1}
              playerDeck={player1Deck}
              moveCard={index =>
                moveToDeck(setPlayer1Cards, setPlayer1Deck, index)
              }
              canMoveCard={(item, card, player) =>
                canMoveToDeck(item, player1Cards, card, player)
              }
            />
          </div>
          <div className={styles.row}>
            <div className={styles.counter}>
              <h2>{player1Cards.length}</h2>
            </div>
            <div className={styles.col}>
              <Slot
                moveCard={item => pickUpDeck(item.idDeck, setPlayer1Cards, 1)}
                canMoveCard={item => canPickUpDeck(item, 1)}
              >
                <DraggableCard
                  card={{ special: player1Cards.length ? 'reverse' : 'slot' }}
                  player={1}
                />
              </Slot>
            </div>
          </div>
        </div>
      </DndProvider>
      {winner && (
        <div className={styles.WinOverly}>
          <div className={styles.Win}>
            <div className={styles.WinText}>{`${winner} Wins !!!`}</div>
            <button type="button" onClick={() => playAgain()}>
              Play again
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Game;
